import {Checkbox, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {PublishingRecordForButton} from 'template-collectibles/publishing-ui/graphql/types';
import TemplatePublishingChipsWithArrow from 'template-collectibles/publishing-ui/TemplatePublishingChipsWithArrow';
import {
  PublishableVersionStatus,
  PUBLISHABLE_VERSION_STATUS_TO_COLOR,
  DEFAULT_VERSION_NAME,
  PublishableVersionAction,
  CONTROLLED_VERSION_NAME_PREFIX,
  AUTO_GENERATED_VERSION_NAME_PREFIX,
  PUBLISHING_RECORD_VERSION_NAME_MAX_LENGTH,
} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import TextArea from 'ui-library/bs/TextArea';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import HelpIcon from 'ui-library/icons/HelpIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

function getButtonPropsFromAction(action: PublishableVersionAction, hasError?: boolean): ModalButton {
  if (action === PublishableVersionAction.PUBLISH) {
    return {text: 'Publish', submit: true, disabled: hasError};
  } else if (action === PublishableVersionAction.WITHDRAW) {
    return {
      text: 'Withdraw',
      submit: true,
      className: css(styles.withdrawActionButton),
    };
  }
  throw new Error(`Invalid action: ${action}`);
}

function getTitleFromAction(action: PublishableVersionAction): string {
  if (action === PublishableVersionAction.PUBLISH) {
    return `Publish version`;
  } else if (action === PublishableVersionAction.WITHDRAW) {
    return `Withdraw version`;
  }
  throw new Error(`Invalid action: ${action}`);
}

function getDescriptionFromAction(
  action: PublishableVersionAction,
  publishableModelTypeName: string,
  hasEffectiveVersion: boolean
): JSX.Element {
  if (action === PublishableVersionAction.PUBLISH) {
    if (hasEffectiveVersion) {
      return (
        <p>
          Publishing this version will make it the <span style={{fontWeight: 'bold'}}>effective</span> version
          of the {publishableModelTypeName} and <span style={{fontWeight: 'bold'}}>available for use</span>.
          Any existing effective version will be <span style={{fontWeight: 'bold'}}>superseded</span>.
        </p>
      );
    }
    return (
      <p>
        Publishing this version will make it the <span style={{fontWeight: 'bold'}}>effective</span> version
        of the {publishableModelTypeName} and <span style={{fontWeight: 'bold'}}>available for use</span>.
      </p>
    );
  } else if (action === PublishableVersionAction.WITHDRAW) {
    return (
      <p>
        Withdrawing the effective version will make this {publishableModelTypeName}{' '}
        <span style={{fontWeight: 'bold'}}>unavailable for use</span> until a new effective version is
        published.
      </p>
    );
  }
  throw new Error(`Invalid action: ${action}`);
}

function getVersionNameErrorMessage(
  versionName: string,
  previousVersionNames: Set<string>
): string | undefined {
  if (previousVersionNames.has(versionName)) {
    return 'Name is already in use';
  }
  if (!versionName) {
    return 'Name is required';
  }
  if (versionName.length > PUBLISHING_RECORD_VERSION_NAME_MAX_LENGTH) {
    return `Maximum ${PUBLISHING_RECORD_VERSION_NAME_MAX_LENGTH} characters`;
  }
  return undefined;
}

// Matches "Version 1" (major only) or "Version 1.2" (major.minor).
const CONTROLLED_VERSION_PATTERN = /^Version ([1-9]\d*)(?:\.([1-9]\d*))?$/;

function parseControlledVersionName(versionName: string): {major: number; minor: number | null} | null {
  const match = CONTROLLED_VERSION_PATTERN.exec(versionName);
  if (!match) {
    return null;
  }
  return {
    major: parseInt(match[1], 10),
    minor: match[2] != null ? parseInt(match[2], 10) : null,
  };
}

// Determines whether the most recent publishing record used a controlled name (major or minor).
function shouldPublishingRecordUseControlledName(
  publishingRecord: PublishingRecordForButton | undefined
): boolean {
  if (publishingRecord == null) {
    return true;
  }
  const {versionName} = publishingRecord;
  // The initial version name should never use the (SYSGEN) prefix
  if (versionName.startsWith(AUTO_GENERATED_VERSION_NAME_PREFIX)) {
    return true;
  }
  return parseControlledVersionName(versionName) != null;
}

function getInitialControlledNameCheckboxState(
  currentPublishingRecord: PublishingRecordForButton | undefined,
  forceControlledVersionName: boolean
): boolean {
  return forceControlledVersionName || shouldPublishingRecordUseControlledName(currentPublishingRecord);
}

// Computes the next major version by finding the highest major number across all records.
// Falls back to index-based calculation when all prior versions used custom names —
// in that case we use the highest existing record index to pick a sensible number.
function getNextMajorVersionName(publishingRecords: Array<PublishingRecordForButton>): string {
  if (publishingRecords.length === 0) {
    return DEFAULT_VERSION_NAME;
  }
  let highestMajor = 0;
  for (const record of publishingRecords) {
    const parsed = parseControlledVersionName(record.versionName);
    if (parsed != null) {
      highestMajor = Math.max(highestMajor, parsed.major);
    }
  }
  if (highestMajor === 0) {
    const maxIndex = Math.max(...publishingRecords.map((r) => r.index));
    return `${CONTROLLED_VERSION_NAME_PREFIX} ${maxIndex + 2}`;
  }
  return `${CONTROLLED_VERSION_NAME_PREFIX} ${highestMajor + 1}`;
}

// Computes the next minor version under the most recent controlled version's major.
// A minor version is a correction to the version currently being superseded, so we
// anchor on the most recent record (e.g. most recent "Version 20" -> "Version 20.1")
// rather than the highest major in history.
function getNextMinorVersionName(publishingRecords: Array<PublishingRecordForButton>): string {
  const currentPublishingRecord = publishingRecords[publishingRecords.length - 1];
  const parsedCurrent =
    currentPublishingRecord != null ? parseControlledVersionName(currentPublishingRecord.versionName) : null;
  // parsedCurrent is always non-null in practice (the checkbox is disabled otherwise).
  // Defensively fall back to major version 1 to avoid producing "Version undefined.1".
  const anchorMajor = parsedCurrent?.major ?? 1;
  let highestMinor = 0;
  for (const record of publishingRecords) {
    const parsed = parseControlledVersionName(record.versionName);
    if (parsed != null && parsed.major === anchorMajor && parsed.minor != null) {
      highestMinor = Math.max(highestMinor, parsed.minor);
    }
  }
  return `${CONTROLLED_VERSION_NAME_PREFIX} ${anchorMajor}.${highestMinor + 1}`;
}

function getControlledVersionName(
  publishingRecords: Array<PublishingRecordForButton>,
  isMinorVersion: boolean
): string {
  if (isMinorVersion) {
    return getNextMinorVersionName(publishingRecords);
  }
  return getNextMajorVersionName(publishingRecords);
}

function getInitialVersionName(
  currentPublishingRecord: PublishingRecordForButton | undefined,
  publishingRecords: Array<PublishingRecordForButton>,
  forceControlledVersionName: boolean
): string {
  if (forceControlledVersionName || shouldPublishingRecordUseControlledName(currentPublishingRecord)) {
    return getNextMajorVersionName(publishingRecords);
  }
  return currentPublishingRecord!.versionName;
}

export interface TemplatePublishingModalProps {
  publishingAction: PublishableVersionAction;
  onRequestSubmit: ({
    versionName,
    versionDescription,
  }: {
    versionName: string;
    versionDescription?: string;
  }) => void;
  onRequestClose: () => void;
  publishableModelTypeName: string;
  publishingRecords: Array<PublishingRecordForButton>;
}

export default function TemplatePublishingModal({
  publishingAction,
  onRequestSubmit,
  onRequestClose,
  publishableModelTypeName,
  publishingRecords,
}: TemplatePublishingModalProps): JSX.Element {
  const forceControlledVersionName = BenchlingConfig.ELN_TEMPLATE_PUBLISHING_RESTRICT_TO_CONTROLLED_NAME;
  const previousVersionNames = useMemo(
    () => new Set(publishingRecords.map((pr) => pr.versionName)),
    [publishingRecords]
  );
  const currentPublishingRecord = publishingRecords[publishingRecords.length - 1];
  const [shouldUseControlledVersionName, setShouldUseControlledVersionName] = React.useState(() =>
    getInitialControlledNameCheckboxState(currentPublishingRecord, forceControlledVersionName)
  );
  const [isMinorVersion, setIsMinorVersion] = React.useState(false);
  const [versionName, setVersionName] = React.useState(() =>
    getInitialVersionName(currentPublishingRecord, publishingRecords, forceControlledVersionName)
  );
  const [versionNameError, setVersionNameError] = React.useState<string | undefined>(
    getVersionNameErrorMessage(versionName, previousVersionNames)
  );
  const [versionDescription, setVersionDescription] = React.useState('');
  const actionButton = getButtonPropsFromAction(publishingAction, Boolean(versionNameError));
  const title = getTitleFromAction(publishingAction);
  const hasEffectiveVersion =
    currentPublishingRecord?.publishingStatus === PublishableVersionStatus.EFFECTIVE;
  const description = getDescriptionFromAction(
    publishingAction,
    publishableModelTypeName,
    hasEffectiveVersion
  );

  const canAttachMinorToMostRecent =
    currentPublishingRecord != null &&
    parseControlledVersionName(currentPublishingRecord.versionName) != null;
  const isMinorVersionDisabled = !shouldUseControlledVersionName || !canAttachMinorToMostRecent;
  const controlledVersionName = useMemo(
    () => getControlledVersionName(publishingRecords, isMinorVersion),
    [publishingRecords, isMinorVersion]
  );
  const handleControlledNameCheckboxChange = useCallback(
    (e: InputChangeEvent): void => {
      const checked = e.target.checked;
      setShouldUseControlledVersionName(checked);
      if (checked) {
        const name = getControlledVersionName(publishingRecords, isMinorVersion);
        setVersionName(name);
        setVersionNameError(getVersionNameErrorMessage(name, previousVersionNames));
      } else {
        setIsMinorVersion(false);
      }
    },
    [publishingRecords, previousVersionNames, isMinorVersion]
  );
  const handleMinorVersionCheckboxChange = useCallback(
    (e: InputChangeEvent): void => {
      const checked = e.target.checked;
      setIsMinorVersion(checked);
      if (shouldUseControlledVersionName) {
        const name = getControlledVersionName(publishingRecords, checked);
        setVersionName(name);
        setVersionNameError(getVersionNameErrorMessage(name, previousVersionNames));
      }
    },
    [publishingRecords, previousVersionNames, shouldUseControlledVersionName]
  );

  return (
    <Modal
      title={title}
      medium={true}
      buttons={[actionButton]}
      onRequestSubmit={() => {
        onRequestSubmit({versionName, versionDescription: versionDescription || undefined});
      }}
      onRequestClose={onRequestClose}
      dataTestAttrs={{component: 'TemplatePublishingModal', element: 'root'}}
    >
      {description}
      {publishingAction === PublishableVersionAction.PUBLISH && (
        <div className={css(styles.versionNameContainer)}>
          {!forceControlledVersionName && (
            <Checkbox
              id="useControlledVersionName"
              checked={shouldUseControlledVersionName}
              label={`Use controlled display name (${controlledVersionName})`}
              onChange={handleControlledNameCheckboxChange}
              {...getTestProps({component: 'TemplatePublishingModal', element: 'checkbox'})}
            />
          )}
          <div className={css(styles.minorVersionCheckboxContainer)}>
            {/* Minor version is disabled when controlled name is not active
                or the most recent version doesn't use a controlled name. The
                tooltip explaining why only renders while the checkbox is disabled. */}
            <Tooltip
              multiline={true}
              width={260}
              disabled={!isMinorVersionDisabled}
              label={
                <div>
                  <p className={css(styles.disabledTooltipHeader)}>To publish a minor version:</p>
                  <ul className={css(styles.disabledTooltipList)}>
                    <li>Use the controlled display name</li>
                    <li>Make sure the most recent version uses a controlled name (e.g. "Version 2")</li>
                  </ul>
                </div>
              }
            >
              <span>
                <Checkbox
                  id="isMinorVersion"
                  checked={isMinorVersion}
                  label="This is a minor version"
                  onChange={handleMinorVersionCheckboxChange}
                  disabled={isMinorVersionDisabled}
                  {...getTestProps({component: 'TemplatePublishingModal', element: 'minorVersionCheckbox'})}
                />
              </span>
            </Tooltip>
            <Tooltip
              multiline={true}
              width={240}
              label={
                <div>
                  <p>
                    Use minor versions for small corrections like typos, formatting, or wording changes. Refer
                    to your organization's SOPs for guidance.
                  </p>
                  <div className={css(styles.tooltipExamples)}>
                    <span>
                      Major: <strong className={css(styles.tooltipVersionChip)}>v3</strong>
                    </span>
                    <span>
                      Minor: <strong className={css(styles.tooltipVersionChip)}>v3.1</strong>
                    </span>
                  </div>
                </div>
              }
            >
              <span>
                <HelpIcon />
              </span>
            </Tooltip>
          </div>
          <TextField
            placeholder="Version display name"
            value={versionName}
            onChange={(e) => {
              setVersionName(e.target.value);
              setVersionNameError(getVersionNameErrorMessage(e.target.value, previousVersionNames));
            }}
            label="Version display name"
            maxLength={PUBLISHING_RECORD_VERSION_NAME_MAX_LENGTH}
            isRequired={true}
            error={
              forceControlledVersionName
                ? undefined
                : {
                    message:
                      versionNameError ?? `Maximum ${PUBLISHING_RECORD_VERSION_NAME_MAX_LENGTH} characters`,
                    style: versionNameError ? InputErrorMessageStyle.ERROR : InputErrorMessageStyle.INFO,
                  }
            }
            hasError={!forceControlledVersionName && Boolean(versionNameError)}
            disabled={shouldUseControlledVersionName}
            dataTestAttrs={{component: 'TemplatePublishingModal', element: 'textField'}}
          />
        </div>
      )}
      <TemplatePublishingChipsWithArrow
        publishingAction={publishingAction}
        currentPublishingRecord={currentPublishingRecord}
        versionName={versionName}
      />
      {publishingAction === PublishableVersionAction.PUBLISH && (
        <div className={css(styles.versionDescriptionContainer)}>
          <FieldLabel label="Description" />
          <TextArea
            onChange={(e) => {
              setVersionDescription(e.target.value);
            }}
            placeholder="Describe what changed in this version"
            value={versionDescription}
            className={css(styles.descriptionTextArea)}
            maxLength={255}
            dataTestAttrs={{component: 'TemplatePublishingModal', element: 'textArea'}}
          />
          <span className={css(styles.maxLengthText)}>Maximum 255 characters</span>
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  maxLengthText: {
    ...FONT_PROXIMA_11,
    color: GRAY_SEMIDARK,
    marginTop: '4px',
  },
  versionDescriptionContainer: {
    marginTop: '16px',
  },
  descriptionTextArea: {
    height: '60px',
  },
  versionNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
  },
  minorVersionCheckboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '4px',
  },
  disabledTooltipHeader: {
    marginBottom: '4px',
  },
  disabledTooltipList: {
    margin: 0,
    paddingLeft: '16px',
  },
  tooltipExamples: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  tooltipVersionChip: {
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
  },
  withdrawActionButton: {
    backgroundColor: PUBLISHABLE_VERSION_STATUS_TO_COLOR[PublishableVersionStatus.WITHDRAWN],
  },
});
